import { app } from "../App";
import { ApplicationWithId } from "../interfaces/IApplications";
import { AppUser } from "../interfaces/IUsers";
import { CategoryWithId } from "../interfaces/ICategories";
import { ContestWithId } from "../interfaces/IContests";
import { getPDFService } from "../services/pdfService";
import { PresentationWithId } from "../interfaces/IPresentations";
import { useState } from "react";

export interface UsePDFReturn {
  isGenerating: boolean;
  error: string | null;
  generateApplicationConfirmation: (
    application: ApplicationWithId,
    contest: ContestWithId,
    categories: CategoryWithId[],
    user: AppUser,
    autoDownload?: boolean,
    presentations?: PresentationWithId[]
  ) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook do obsługi generowania PDF
 *
 * Funkcjonalności:
 * - Stan ładowania podczas generowania
 * - Obsługa błędów
 * - Generowanie potwierdzenia zgłoszenia
 * - Automatyczne pobieranie lub otwieranie w nowej karcie
 */
export const usePDF = (): UsePDFReturn => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generowanie potwierdzenia zgłoszenia
   */
  const generateApplicationConfirmation = async (
    application: ApplicationWithId,
    contest: ContestWithId,
    categories: CategoryWithId[],
    user: AppUser,
    autoDownload = true,
    presentations?: PresentationWithId[]
  ): Promise<void> => {
    setIsGenerating(true);
    setError(null);

    try {
      const pdfService = getPDFService(app);

      if (autoDownload) {
        // Generuj i pobierz automatycznie
        await pdfService.generateAndDownloadConfirmation(
          application,
          contest,
          categories,
          user,
          presentations
        );
      } else {
        // Generuj i otwórz w nowej karcie
        const response = await pdfService.generateApplicationConfirmation(
          application,
          contest,
          categories,
          user,
          presentations
        );
        await pdfService.openPDFInNewTab(response.pdfBase64);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Nieznany błąd podczas generowania PDF";
      console.error("❌ [usePDF] Błąd podczas generowania PDF:", {
        error: err,
        message: errorMessage,
        applicationId: application.id,
      });
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Czyszczenie błędu
   */
  const clearError = (): void => {
    setError(null);
  };

  return {
    isGenerating,
    error,
    generateApplicationConfirmation,
    clearError,
  };
};
